import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, imgUrl } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Sprout, Leaf, ArrowUpDown, Filter, Package, Weight, Euro, MapPin, Sparkles, X, Plus } from 'lucide-react';
import BatchFavoriteButton from '@/components/BatchFavoriteButton';
import Mascot from '@/components/Mascot';
import YieldAiBanner from '@/components/YieldAiBanner';

const fmt = (n) => n != null ? new Intl.NumberFormat('it-IT').format(n) : '—';

const QUALITY_LABEL = {
  standard: 'Standard',
  biologico: 'Biologico',
  igp: 'IGP',
  dop: 'DOP',
  in_conversione: 'In conversione bio',
};
const QUALITY_COLOR = {
  biologico: 'bg-chartreuse text-obsidian',
  dop: 'bg-terracotta text-white',
  igp: 'bg-foreground text-background',
  standard: 'bg-secondary text-foreground',
  in_conversione: 'bg-secondary text-foreground',
};
const PACKAGING_LABEL = {
  sfuso: 'Sfuso',
  cassette: 'In cassette',
  bins: 'Bins',
  sacchi: 'In sacchi',
  contenitori_industriali: 'Contenitori industriali',
};

function BatchCard({ b }) {
  const img = b.images?.[0] ? imgUrl(b.images[0]) : null;
  return (
    <Link
      to={`/partite/${b.batch_id}`}
      data-testid={`batch-card-${b.batch_id}`}
      className="group block rounded-2xl overflow-hidden border border-border bg-card hover:shadow-[0_12px_40px_rgba(0,0,0,0.06)] hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-secondary/40">
        {img ? (
          <img src={img} alt={b.fruit_type} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
        ) : (
          <div className="w-full h-full grid place-items-center">
            <Sprout size={56} className="opacity-15" strokeWidth={1.2}/>
          </div>
        )}
        <div className="absolute top-3 left-3 flex gap-2 flex-wrap max-w-[85%]">
          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${QUALITY_COLOR[b.quality_grade] || 'bg-background/90'}`}>
            {QUALITY_LABEL[b.quality_grade] || b.quality_grade}
          </span>
        </div>
        <div className="absolute top-3 right-3">
          <BatchFavoriteButton batchId={b.batch_id}/>
        </div>
      </div>
      <div className="p-5">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{b.fruit_type}</div>
        <h3 className="font-display text-xl leading-tight mt-1 line-clamp-1">
          {b.variety || b.fruit_type}
        </h3>
        <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Weight size={13}/> <span>{fmt(b.quantity_quintals)} q</span>
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Package size={13}/> <span>{PACKAGING_LABEL[b.packaging] || b.packaging || '—'}</span>
          </div>
        </div>
        {b.location?.comune && (
          <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin size={12}/> {[b.location.comune, b.location.region].filter(Boolean).join(', ')}
          </div>
        )}
        <div className="mt-4 pt-4 border-t border-border flex items-end justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Prezzo</div>
            <div className="font-display text-2xl">€ {fmt(b.price_per_quintal)}<span className="text-xs text-muted-foreground font-sans">/q</span></div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Totale</div>
            <div className="text-sm font-medium">€ {fmt(b.total_price)}</div>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function Batches() {
  const [meta, setMeta] = useState({ fruit_types: [], quality_grades: [], packaging_types: [] });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [params, setParams] = useSearchParams();

  const f = {
    fruit_type: params.get('fruit_type') || '',
    quality: params.get('quality') || '',
    packaging: params.get('packaging') || '',
    price_min: params.get('price_min') || '',
    price_max: params.get('price_max') || '',
    qty_min: params.get('qty_min') || '',
    qty_max: params.get('qty_max') || '',
    region: params.get('region') || '',
    q: params.get('q') || '',
    sort: params.get('sort') || 'recent',
  };
  const setF = (k, v) => {
    const p = new URLSearchParams(params);
    if (v === '' || v == null) p.delete(k); else p.set(k, v);
    setParams(p);
  };
  const clearAll = () => setParams(new URLSearchParams());
  const activeCount = Object.entries(f).filter(([k,v])=>v && k!=='sort').length;

  useEffect(() => { api.get('/batches/meta').then(r => setMeta(r.data)); }, []);
  useEffect(() => {
    // Non svuotare gli items subito: mantieni i precedenti finché arriva la nuova risposta
    // (evita "scroll jumping" quando la lista cambia di dimensione).
    setLoading(true);
    let cancelled = false;
    api.get('/batches', { params: Object.fromEntries(params) })
      .then(r => { if (!cancelled) { setItems(r.data.items || []); setTotal(r.data.total || 0); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [params]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div className="flex items-end gap-4">
          <Mascot pose="basket" size={110} className="hidden md:block shrink-0 -mb-1" data-testid="batches-hero-mascot"/>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-2">Marketplace prodotti</div>
            <h1 className="font-display text-4xl md:text-5xl tracking-tighter flex items-center gap-3">
              <Leaf size={38} strokeWidth={1.4} className="text-chartreuse"/> Partite
            </h1>
            <p className="mt-2 text-sm text-muted-foreground max-w-xl">
              Trova o vendi partite di frutta, ortaggi e cereali. Filtra per tipo, quantità, qualità e prezzo al quintale.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/partite/suggerite" data-testid="batches-suggested-link" className="hidden sm:inline-flex">
            <Button variant="outline" className="rounded-full"><Sparkles size={14} className="mr-1.5"/> Suggerite per te</Button>
          </Link>
          <div className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground border border-border rounded-full px-3 py-1.5">
            <ArrowUpDown size={12}/>
            <select
              value={f.sort}
              onChange={e=>setF('sort', e.target.value)}
              data-testid="batches-sort"
              className="bg-transparent focus:outline-none"
            >
              <option value="recent">Più recenti</option>
              <option value="price_asc">Prezzo crescente</option>
              <option value="price_desc">Prezzo decrescente</option>
              <option value="qty_desc">Maggior quantità</option>
            </select>
          </div>
          <Link to="/partite/pubblica" data-testid="batches-publish-cta">
            <Button className="rounded-full"><Plus size={14} className="mr-1.5"/> Pubblica partita</Button>
          </Link>
        </div>
      </div>

      {/* Yield AI PRO cross-promo compact banner */}
      <YieldAiBanner variant="compact" className="mb-6"/>

      <div className="grid md:grid-cols-4 gap-8">
        <aside className="md:col-span-1">
          <div className="rounded-2xl border border-border bg-card p-5 md:sticky md:top-24">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-lg flex items-center gap-2">
                <Filter size={16}/> Filtri
                {activeCount > 0 && (
                  <span className="text-[10px] font-bold bg-chartreuse text-obsidian rounded-full px-2 py-0.5">{activeCount}</span>
                )}
              </div>
              {activeCount > 0 && (
                <button onClick={clearAll} data-testid="batches-clear-filters" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                  <X size={11}/> Reset
                </button>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest">Cosa cerchi</Label>
                <Input
                  value={f.q}
                  onChange={e=>setF('q', e.target.value)}
                  placeholder="Es. Sangiovese, biologico…"
                  data-testid="batches-filter-q"
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest">Tipo prodotto</Label>
                <select
                  value={f.fruit_type}
                  onChange={e=>setF('fruit_type', e.target.value)}
                  data-testid="batches-filter-fruit"
                  className="mt-1.5 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Tutti</option>
                  {meta.fruit_types.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest">Qualità</Label>
                <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                  {meta.quality_grades.map(qg => (
                    <button
                      key={qg}
                      type="button"
                      onClick={()=>setF('quality', f.quality === qg ? '' : qg)}
                      data-testid={`batches-filter-quality-${qg}`}
                      className={`text-xs px-2.5 py-1.5 rounded-full border ${f.quality===qg ? 'bg-foreground text-background border-foreground' : 'border-border hover:bg-secondary'}`}
                    >
                      {QUALITY_LABEL[qg]}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest">Confezionamento</Label>
                <select
                  value={f.packaging}
                  onChange={e=>setF('packaging', e.target.value)}
                  data-testid="batches-filter-packaging"
                  className="mt-1.5 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Tutti</option>
                  {meta.packaging_types.map(p => <option key={p} value={p}>{PACKAGING_LABEL[p] || p}</option>)}
                </select>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest flex items-center gap-1"><Euro size={11}/> Prezzo al quintale</Label>
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                  <Input placeholder="Min" type="number" value={f.price_min} onChange={e=>setF('price_min', e.target.value)}/>
                  <Input placeholder="Max" type="number" value={f.price_max} onChange={e=>setF('price_max', e.target.value)}/>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest flex items-center gap-1"><Weight size={11}/> Quantità (q)</Label>
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                  <Input placeholder="Min" type="number" value={f.qty_min} onChange={e=>setF('qty_min', e.target.value)}/>
                  <Input placeholder="Max" type="number" value={f.qty_max} onChange={e=>setF('qty_max', e.target.value)}/>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest">Regione</Label>
                <Input value={f.region} onChange={e=>setF('region', e.target.value)} placeholder="Es. Toscana"/>
              </div>
            </div>
          </div>
        </aside>

        <section className="md:col-span-3">
          <div className="text-sm text-muted-foreground mb-4">
            {loading ? 'Caricamento…' : `${total} partit${total===1?'a':'e'} trovat${total===1?'a':'e'}`}
          </div>
          {items.length === 0 && !loading ? (
            <div className="rounded-2xl border border-dashed border-border p-12 text-center">
              <Sparkles size={32} className="mx-auto opacity-40 mb-3" strokeWidth={1.4}/>
              <div className="font-display text-xl">Nessuna partita corrisponde ai filtri</div>
              <div className="text-sm text-muted-foreground mt-1">Rimuovi qualche filtro o sii il primo a pubblicare!</div>
              <Link to="/partite/pubblica" className="inline-block mt-4">
                <Button className="rounded-full">Pubblica la prima partita</Button>
              </Link>
            </div>
          ) : items.length === 0 && loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {[0,1,2,3].map(i=><div key={i} className="rounded-2xl h-72 bg-secondary/40 animate-pulse"/>)}
            </div>
          ) : (
            <div className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 transition-opacity ${loading?'opacity-60':'opacity-100'}`}>
              {items.map(b => <BatchCard key={b.batch_id} b={b}/>)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
